import type { HashtagCategory } from '../CollapsibleHashtags/CollapsibleHashtags';
import type { CreatorEntry } from '../CollapsibleCreators/CollapsibleCreators';
import type { BrandEntry } from '../CollapsibleBrands/CollapsibleBrands';

type HashtagTag = string | { text?: string; link?: string };

const isFilledString = (value: unknown): boolean =>
  typeof value === 'string' && value.trim().length > 0;

const toArray = <T>(value: unknown): T[] =>
  Array.isArray(value) ? (value as T[]) : [];

export function isFilledHashtag(tag: unknown): boolean {
  if (typeof tag === 'string') {
    return tag.trim().length > 0;
  }
  if (tag !== null && typeof tag === 'object') {
    const t = tag as { text?: unknown };
    return isFilledString(t.text);
  }
  return false;
}

export function isFilledHashtagCategory(category: HashtagCategory | null | undefined): boolean {
  if (!category) {
    return false;
  }

  return toArray<HashtagTag>(category.hashtags).some(isFilledHashtag);
}

export function getFilledHashtagCategories(
  categories: HashtagCategory[] | null | undefined | unknown
): HashtagCategory[] {
  type HashtagItem = string | { text?: string; link?: string };
  return toArray<HashtagCategory>(categories)
    .filter(isFilledHashtagCategory)
    .map((category) => ({
      ...category,
      hashtags: toArray<HashtagTag>(category.hashtags).filter(isFilledHashtag) as HashtagItem[],
    }));
}

export function isFilledCreator(creator: CreatorEntry | null | undefined): boolean {
  if (!creator) {
    return false;
  }

  return (
    isFilledString(creator.name) ||
    isFilledString(creator.username) ||
    isFilledString(creator.imageUrl)
  );
}

export function getFilledCreators(
  creators: CreatorEntry[] | null | undefined | unknown
): CreatorEntry[] {
  return toArray<CreatorEntry>(creators).filter(isFilledCreator);
}

export function isFilledBrand(brand: BrandEntry | null | undefined): boolean {
  if (!brand) {
    return false;
  }

  return (
    isFilledString(brand.name) ||
    isFilledString(brand.username) ||
    isFilledString(brand.imageUrl)
  );
}

export function getFilledBrands(
  brands: BrandEntry[] | null | undefined | unknown
): BrandEntry[] {
  return toArray<BrandEntry>(brands).filter(isFilledBrand);
}

export function sanitizeTiktokTrendsProps(props: Record<string, unknown>): Record<string, unknown> {
  const hashtagCategories = getFilledHashtagCategories(props.hashtagCategories);
  const creators = getFilledCreators(props.creators);
  const brands = getFilledBrands(props.brands);

  return {
    ...(hashtagCategories.length > 0 ? { hashtagCategories } : {}),
    ...(creators.length > 0 ? { creators } : {}),
    ...(brands.length > 0 ? { brands } : {}),
  };
}
